import React, { useState, useMemo } from "react";
import EscReportFilter from "./EscReportFilter";
import SpinnerController from "../spinnerController/Spinner";
import Pagination from "./Pagination";
const EscReportTable = ({
  project,
  handleFilter,
  ticketDetails,
  setProject,
  filterError,
  years,
  setYear,
  setQuarter,
  setSuperArea,
  setSegment,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const filteredTickets = useMemo(() => {
    if (!ticketDetails.tickets) return [];
    const filtered = ticketDetails.tickets.filter(
      (ticket) =>
        ticket.ticket?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const sortableItems = [...filtered];
    sortableItems.sort((a, b) => {
      if (a[sortConfig.key] === undefined || b[sortConfig.key] === undefined) {
        return 0;
      }
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";
      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
    return sortableItems;
  }, [ticketDetails.tickets, searchQuery, sortConfig]);
  const getSortArrow = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === "asc" ? "▲" : "▼";
    }
    return "▲";
  };
  return (
    <>
      <div
        className="max-w-6xl flex flex-col px-10 justify-center container mx-auto pt-10"
        style={{ maxWidth: "1700px" }}
      >
        <div className="w-full text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-700">
            Escalation Breakdown Report
          </h1>
        </div>
        <div className="table-wrp block max-h-[41rem] overflow-x-auto relative shadow-md sm:rounded-lg bg-gray-500 w-full">
          <table className="w-full text-sm text-center text-gray-500">
            <caption className="sticky top-0 p-5 text-lg font-semibold text-left text-gray-700 bg-white border-b-2 border-gray-500">
              <div className="flex items-center justify-center aligncenter justify-between">
                <EscReportFilter
                  project={project}
                  handleFilter={handleFilter}
                  setProject={setProject}
                  setYear={setYear}
                  years={years}
                  setQuarter={setQuarter}
                  setSuperArea={setSuperArea}
                  setSegment={setSegment}
                />
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      aria-hidden="true"
                      className="w-5 h-5 text-gray-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="w-64 p-0.5 border border-gray-300 rounded text-center pl-10"
                    placeholder="Search by name or email"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div>
                {filterError && (
                  <h6 className="pt-2 text-xs font-sm text-red-700">
                    * {filterError}
                  </h6>
                )}
              </div>
            </caption>
            <thead className="sticky top-[88px] text-xs text-gray-900 uppercase bg-white border-b-2 border-gray-500">
              <tr>
                <th scope="col" className="py-3 px-6 no-wrap">Case</th>
                <th scope="col" className="py-3 px-6 no-wrap">Status</th>
                <th
                  scope="col"
                  className="py-3 px-6 no-wrap cursor-pointer"
                  onClick={() => requestSort("email")}
                >
                  Escalated By {getSortArrow("email")}
                </th>
                <th
                  scope="col"
                  className="py-3 px-6 no-wrap cursor-pointer"
                  onClick={() => requestSort("role")}
                >
                  Role {getSortArrow("role")}
                </th>
                <th
                  scope="col"
                  className="py-3 px-6 no-wrap cursor-pointer"
                  onClick={() => requestSort("super_area")}
                >
                  SuperArea {getSortArrow("super_area")}
                </th>
                <th scope="col" className="py-3 px-6 no-wrap">Location</th>
                <th
                  scope="col"
                  className="py-3 px-6 no-wrap cursor-pointer"
                  onClick={() => requestSort("supportLevel")}
                >
                  Segment {getSortArrow("supportLevel")}
                </th>
              </tr>
            </thead>
            <tbody>
              {!ticketDetails.tickets ? (
                <tr key="loading">
                  <td colSpan="7" className="bg-gray-50 p-4">
                    <div className="flex justify-center">
                      <SpinnerController />
                    </div>
                  </td>
                </tr>
              ) : !filteredTickets.length ? (
                <tr key="nodata">
                  <td className="bg-gray-50 p-4" colSpan="7">
                    No data available
                  </td>
                </tr>
              ) : (
                filteredTickets.map((ticket, i) => (
                  <tr
                    key={i}
                    className="bg-white text-gray-900 border-b hover:bg-myellow"
                  >
                    <td className="py-4 px-6 bg-myellow font-medium font-semibold text-gray-900 no-wrap">
                      {ticket.ticket}
                    </td>
                    <td className="py-4 px-6 font-medium font-semibold text-gray-900 no-wrap">
                      {ticket.isOpened ? "Open" : "Closed"}
                    </td>
                    <td className="py-4 px-6 font-medium font-semibold text-gray-900 no-wrap">
                      {ticket.email}
                    </td>
                    <td className="py-4 px-6 font-medium font-semibold text-gray-900 no-wrap">
                      {ticket.role || ""}
                    </td>
                    <td className="py-4 px-6 font-medium font-semibold text-gray-900 no-wrap">
                      {ticket.super_area}
                    </td>
                    <td className="py-4 px-6 font-medium font-semibold text-gray-900 no-wrap">
                      {ticket.location}
                    </td>
                    <td className="py-4 px-6 font-medium font-semibold text-gray-900 no-wrap">
                      {ticket.supportLevel}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination
        project={project}
        pageNo={ticketDetails?.tickets?.length || 0}
        length={ticketDetails.tLength}
      />
    </>
  );
};
export default EscReportTable;